/**
 * Compute backend `is_custom` / `override_type` for a self-schedule
 * slot at payload-build time.
 *
 * --------------------------------------------------------------------
 * VISUAL CUE → override_type (what QA sees, what we emit)
 * --------------------------------------------------------------------
 * Map the user's drop location on the FullCalendar grid to the wire value:
 *
 *   - Free white space (inside WH, no busy event)  → `'none'`
 *   - Grey OOO band (outside WH, no busy event)    → `'working_hours'`
 *   - Busy event sitting inside WH (white space)   → `'calendar'`
 *   - Grey OOO band that also has a busy event     → `'all'`
 *
 * "Outside WH" is computed against the *aggregated* per-interviewer
 * schedule (union for 1:1, intersection for panel), NOT the common-
 * availability output the calendar paints. See the helper below.
 *
 * --------------------------------------------------------------------
 * Why this lives at payload-time (not per-tick during drag)
 * --------------------------------------------------------------------
 *   - Avoids state churn while the user is still moving the slot.
 *   - Save-time inputs (busy events + per-interviewer working hours)
 *     are snapshotted into `payloadRef.current.details._overrideContext`
 *     by `DetailsStep.syncToPayload`, so the mapper has everything in
 *     one pure call.
 *
 * --------------------------------------------------------------------
 * How the inputs are captured (data flow)
 * --------------------------------------------------------------------
 *   1. `CalendarPanel` fetches busy + raw per-interviewer working hours
 *      across the visible week + lookahead (multi-week for self-schedule
 *      so picks in future weeks are classified correctly).
 *   2. Once both queries settle, `CalendarPanel.onScheduleContextChange`
 *      fires with `{ workingHoursByInterviewer, busyEvents, isReady }`.
 *      Gated by a content-key compare so it doesn't loop with the
 *      parent setter (sch-19 trap — see SELF_SCHEDULE_BEHAVIOR.md).
 *   3. `DetailsStep.handleScheduleContextChange` stores it in
 *      `scheduleContextRef` (a `useRef`, not state — we only need it
 *      at save time, and routing through state would re-render the
 *      whole tree on every busy refetch).
 *   4. `DetailsStep.syncToPayload` copies the ref into
 *      `payloadRef.current.details._overrideContext` ONLY when the
 *      ready flag is true. If the user clicks save before the context
 *      is ready, the key is omitted so `mapDetails` falls through to
 *      the slot's stored fields (no fabricated `'none'`).
 *   5. `mapDetails` calls `computeSlotOverride()` once per slot.
 *
 * --------------------------------------------------------------------
 * Why we read raw per-interviewer working hours (not common availability)
 * --------------------------------------------------------------------
 * Legacy `KInterviewScheduler` uses each interviewer's own schedule and
 * applies union (1:1) or intersection (panel) at classification time —
 * see `useSchedulerDetailsPayload.tsx#185-215`. The wizard's
 * `availability` prop is already common AND has busy subtracted, so we
 * can't recover the "outside any interviewer's working hours" signal
 * from it. The raw map preserves that detail.
 *
 * --------------------------------------------------------------------
 * Backend semantics — `core/app/models/ats_schedule_slot.rb`
 * (`SKIP_*_TYPES`, kula/core CORE-4352)
 * --------------------------------------------------------------------
 *
 *   | override_type            | Skips calendar | WH | buffer |
 *   | ------------------------ | -------------- | -- | ------ |
 *   | `none`                   |       —        | —  |   —    |
 *   | `calendar`               |       ✓        | —  |   —    |
 *   | `working_hours`          |       —        | ✓  |   —    |
 *   | `calendar_working_hours` |       ✓        | ✓  |   —    |
 *   | `buffer`                 |       —        | —  |   ✓    |
 *   | `buffer_calendar`        |       ✓        | —  |   ✓    |
 *   | `buffer_working_hours`   |       —        | ✓  |   ✓    |
 *   | `wh_cal_buffer`          |       ✓        | ✓  |   ✓    |
 *
 *   The "buffer" filter only exists when the schedule's
 *   `interview_buffer_config` is enabled with non-zero pre/post
 *   minutes — the BE inflates the booked-interview overlap window by
 *   those minutes unless the slot's `override_type` opts out via
 *   `SKIP_BUFFER_TYPES`. We classify a slot as a buffer override when
 *   the *inflated* window overlaps a busy event but the *raw* window
 *   does not (a raw overlap is already a `calendar` override and the
 *   BE skip subsumes buffer). So `buffer ⇒ !calendar` here, which
 *   means `buffer_calendar` / `wh_cal_buffer` are not produced by the
 *   classifier — they exist for wire/round-trip completeness only.
 *
 *   Plus a backward-compat default: `is_custom=true && override_type=none`
 *   resolves to `effective="calendar_working_hours"` server-side. We
 *   don't rely on that; we always send the explicit type and pair
 *   `is_custom` to match.
 */
import type { BusyEvent, Interval, InterviewKind } from '../availability';
import type { SlotOverrideType } from '../calendar/selfSchedule/types';

/** Per-interviewer working-hours map, keyed by stringified user id. */
export type WorkingHoursByInterviewer = Record<string, Interval[]>;

export interface ComputeSlotOverrideArgs {
  slotStart: string;
  slotEnd: string;
  busyEvents: BusyEvent[];
  workingHoursByInterviewer: WorkingHoursByInterviewer;
  interviewerIds: ReadonlyArray<string | number>;
  interviewKind: InterviewKind;
  /**
   * Schedule-level interview buffer. Must mirror exactly what
   * `mapDetails` emits as `interview_buffer_config` (same enabled
   * gate + pre/post minutes) so the FE classification matches the
   * window the BE inflates. Omitted / disabled / 0-0 → no buffer
   * dimension (behaves exactly as before this change).
   */
  bufferEnabled?: boolean;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  /**
   * Spans the caller actually holds working-hours + busy data for.
   * A slot outside every span can't be classified — we return `null`
   * so the caller preserves whatever the slot already carried rather
   * than reading "no interval covers this day" as "outside working
   * hours" (ENG-3571: that guess ships `working_hours`, which leaves
   * the BE calendar filter armed and hides the slot from the
   * candidate). Omit to skip the check entirely.
   */
  coveredRanges?: ReadonlyArray<Interval>;
}

export interface SlotOverrideResult {
  is_custom: boolean;
  override_type: SlotOverrideType;
}

const NONE: SlotOverrideResult = { is_custom: false, override_type: 'none' };

/**
 * `true` when `[startMs, endMs)` intersects any busy event's interval.
 * Standard half-open interval test.
 */
function overlapsAnyBusy(
  startMs: number,
  endMs: number,
  busyEvents: BusyEvent[]
): boolean {
  for (const evt of busyEvents) {
    const evtStart = Date.parse(evt.start);
    const evtEnd = Date.parse(evt.end);
    if (!Number.isFinite(evtStart) || !Number.isFinite(evtEnd)) continue;
    if (startMs < evtEnd && endMs > evtStart) return true;
  }
  return false;
}

/**
 * `true` when the slot is fully contained in at least one of the
 * interviewer's working-hours windows. Mirrors legacy
 * `isSlotWithinInterviewerAvailability`.
 */
function slotFullyInsideAnyInterval(
  startMs: number,
  endMs: number,
  intervals: ReadonlyArray<Interval>
): boolean {
  for (const win of intervals) {
    const winStart = Date.parse(win.start);
    const winEnd = Date.parse(win.end);
    if (!Number.isFinite(winStart) || !Number.isFinite(winEnd)) continue;
    if (startMs >= winStart && endMs <= winEnd) return true;
  }
  return false;
}

/**
 * `true` when the slot falls outside the interviewers' working hours
 * per the kind's aggregation rule:
 *
 *   - `one_on_one` → outside iff NO interviewer has the slot inside
 *     their own working hours (union semantics — at least one is
 *     enough to count as "inside").
 *   - `panel`      → outside iff ANY interviewer doesn't have the slot
 *     inside their working hours (intersection semantics — all must
 *     be inside).
 *
 * If we have no working-hours data for any selected interviewer we
 * return `false` (treat as "in-hours" — no signal, no override). This
 * matches legacy: empty `availabilityByInterviewer[id]` causes
 * `isSlotWithinInterviewerAvailability` to return false, but the
 * legacy aggregate logic only fires the override when ALL evidence is
 * negative — partial evidence shouldn't flag a slot. Same rationale
 * here: don't fabricate an override from missing data.
 */
function isOutsideWorkingHours(
  startMs: number,
  endMs: number,
  workingHoursByInterviewer: WorkingHoursByInterviewer,
  interviewerIds: ReadonlyArray<string | number>,
  interviewKind: InterviewKind
): boolean {
  if (interviewerIds.length === 0) return false;
  const idsWithSchedule = interviewerIds.filter((id) => {
    const intervals = workingHoursByInterviewer[String(id)];
    return Array.isArray(intervals) && intervals.length > 0;
  });
  if (idsWithSchedule.length === 0) return false;

  if (interviewKind === 'one_on_one') {
    // Union — outside only if NOBODY has it inside.
    const anyInside = idsWithSchedule.some((id) =>
      slotFullyInsideAnyInterval(
        startMs,
        endMs,
        workingHoursByInterviewer[String(id)] ?? []
      )
    );
    return !anyInside;
  }

  // Panel — intersection. Outside if ANY of the scheduled interviewers
  // doesn't have it inside their hours. Interviewers without schedule
  // data are skipped (same as legacy — no data, no veto).
  const allInside = idsWithSchedule.every((id) =>
    slotFullyInsideAnyInterval(
      startMs,
      endMs,
      workingHoursByInterviewer[String(id)] ?? []
    )
  );
  return !allInside;
}

/**
 * Maps `(overlapsBusy, outsideWorkingHours, overlapsBuffer)` →
 * `override_type`. Extends legacy `getOverrideType` with the buffer
 * dimension (kula/core CORE-4352).
 *
 * Truth table — also the QA spec (`buffer` only when buffer is
 * enabled AND the inflated window overlaps a busy event the raw
 * window does not — see file header):
 *   cal / WH / buf →  override_type
 *   F   / F  / F   →  'none'
 *   T   / F  / F   →  'calendar'
 *   F   / T  / F   →  'working_hours'
 *   T   / T  / F   →  'calendar_working_hours'
 *   F   / F  / T   →  'buffer'
 *   T   / F  / T   →  'buffer_calendar'
 *   F   / T  / T   →  'buffer_working_hours'
 *   T   / T  / T   →  'wh_cal_buffer'
 */
function combine(
  overlapsBusy: boolean,
  outsideHours: boolean,
  overlapsBuffer: boolean
): SlotOverrideType {
  if (overlapsBuffer) {
    if (overlapsBusy && outsideHours) return 'wh_cal_buffer';
    if (overlapsBusy) return 'buffer_calendar';
    if (outsideHours) return 'buffer_working_hours';
    return 'buffer';
  }
  if (overlapsBusy && outsideHours) return 'calendar_working_hours';
  if (overlapsBusy) return 'calendar';
  if (outsideHours) return 'working_hours';
  return 'none';
}

/**
 * Pure compute. Given a slot + the snapshot of busy events + raw
 * per-interviewer working hours, return `{ is_custom, override_type }`
 * to send to the backend, or `null` when `coveredRanges` says we hold
 * no data for the slot's window (caller keeps the slot's existing
 * classification).
 *
 *   - `none`              → `{ is_custom: false, override_type: 'none' }`
 *   - any non-`none` type → `{ is_custom: true,  override_type: <type> }`
 *
 * `is_custom: true` mirrors legacy: any override-bearing slot was a
 * recruiter override. Leaving `is_custom` aligned with the type avoids
 * the `is_custom=true / override_type=none` backward-compat default
 * from the backend ever activating, which would silently expand the
 * skip set to `calendar_working_hours` — see `effective_override_type`
 * in `ats_schedule_slot.rb`.
 */
export function computeSlotOverride({
  slotStart,
  slotEnd,
  busyEvents,
  workingHoursByInterviewer,
  interviewerIds,
  interviewKind,
  bufferEnabled,
  bufferBeforeMinutes,
  bufferAfterMinutes,
  coveredRanges,
}: ComputeSlotOverrideArgs): SlotOverrideResult | null {
  const startMs = Date.parse(slotStart);
  const endMs = Date.parse(slotEnd);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return NONE;

  // Outside the fetched window there is no evidence either way — say
  // so instead of inventing one. See `coveredRanges` above.
  if (
    coveredRanges &&
    !slotFullyInsideAnyInterval(startMs, endMs, coveredRanges)
  ) {
    return null;
  }

  const overlapsBusy = overlapsAnyBusy(startMs, endMs, busyEvents);
  const outsideHours = isOutsideWorkingHours(
    startMs,
    endMs,
    workingHoursByInterviewer,
    interviewerIds,
    interviewKind
  );

  // Buffer override: only when the schedule buffer is enabled with
  // non-zero pre/post AND the raw slot is busy-clear (a raw overlap is
  // already a `calendar` override, whose BE skip subsumes buffer) but
  // the buffer-inflated `[start-pre, end+post)` window catches a busy
  // event. Mirrors `Calendar::BufferSlotFilter.overlap_window`.
  const pre = Math.max(0, Math.trunc(bufferBeforeMinutes ?? 0));
  const post = Math.max(0, Math.trunc(bufferAfterMinutes ?? 0));
  const overlapsBuffer =
    bufferEnabled === true &&
    !overlapsBusy &&
    (pre > 0 || post > 0) &&
    overlapsAnyBusy(startMs - pre * 60_000, endMs + post * 60_000, busyEvents);

  const override_type = combine(overlapsBusy, outsideHours, overlapsBuffer);
  if (override_type === 'none') return NONE;
  return { is_custom: true, override_type };
}
